import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { DefaultIcon, FileContentType, FileType, IFileReference } from '@summit/core';
import { SummitIdObject } from '@summit/models';

import { BlurDirective, SUiMenubarComponent } from '@summit/ui';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { MenuItem } from 'primeng/api';
import { Button } from 'primeng/button';
import { Image } from 'primeng/image';
import { Skeleton } from 'primeng/skeleton';
import { BehaviorSubject, combineLatest, firstValueFrom, map, Observable, of, tap } from 'rxjs';
import { SFormInputFileComponent } from '../s-form-input-file/s-form-input-file.component';

@Component({
  selector: 's-form-file-viewer',
  templateUrl: './s-form-file-viewer.component.html',
  styleUrls: ['./s-form-file-viewer.component.scss'],
  standalone: true,
  imports: [NgxExtendedPdfViewerModule, Skeleton, AsyncPipe, SUiMenubarComponent, BlurDirective, Image, Button],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SFormFileViewerComponent<T extends SummitIdObject> implements OnInit, OnChanges, OnDestroy {
  //@Input() loaded$: Observable<boolean>;
  @Input() files$: Observable<IFileReference[]>;
  @Input() contentTypes: FileContentType[];
  @Input() fileUpload: SFormInputFileComponent<T>;
  @Input() showDelete: boolean;
  filteredFiles$: Observable<IFileReference[]>;
  fileType = FileType;
  fileMenu$: Observable<MenuItem[]>;
  selectedFileIndexBs: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  selectedFileIndex$: Observable<number> = this.selectedFileIndexBs.asObservable();
  selectedFile$: Observable<IFileReference>;

  defaultIcon = DefaultIcon;

  constructor(
    private sanitizer: DomSanitizer,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.filteredFiles$ = this.files$?.pipe(
      map((files) =>
        !this.contentTypes ? files : files.filter((file) => this.contentTypes.includes(file?.contentType))
      )
    );
    this.changeFileView(0);
    this.startFileWatcher();
  }

  // need for FileUpload to update the file viewer when new files are added or removed
  ngOnChanges() {
    this.startFileWatcher();
  }

  startFileWatcher() {
    this.selectedFile$ = combineLatest([
      this.filteredFiles$,
      this.selectedFileIndex$,
      this.fileUpload?.fileRefs$ ?? of([])
    ]).pipe(
      map(([files, index, fileUploadRefs]) => {
        const lclFileRef = index > files.length ? undefined : files[index];
        if (!lclFileRef) return null;
        const selFileRef = fileUploadRefs.find((f) => f.path === lclFileRef.path);
        //console.log('selectedFile$', selFileRef.blobPath);
        return selFileRef;
      }),
      tap((_) => this.cd.detectChanges())
    );
  }

  getFileChoices(refs: IFileReference[]): MenuItem[] {
    const response = [];
    refs.forEach((fileRef, index) => {
      if (fileRef) {
        response.push({
          label: fileRef?.name,
          command: () => {
            this.changeFileView(index);
          }
        });
      }
    });
    return response;
  }

  changeFileView(index: number) {
    this.selectedFileIndexBs.next(index);
  }

  // getFileRef(index: number): IFileReference {
  //   console.log('getFileRef', index, this.fileUpload?.fileRefs);
  //   const files = this.fileUpload?.fileRefs;
  //   if (!files || files.length <= 0) return null;
  //   return this.fileUpload.fileRefs[index];
  // }

  getFileBlobPath(fileRef: IFileReference): string | ArrayBuffer {
    //console.log('getFileBlobPath', fileRef);
    const fileUploadFile = this.fileUpload.fileMap.get(fileRef.path);
    if (!fileUploadFile) {
      this.refreshAfterBlobLoad(fileRef.path);
    }
    //console.log('getFileBlobPath', fileUploadFile);
    return fileUploadFile?.blobPath;
  }

  refreshAfterBlobLoad(path: string) {
    const fileUploadFile = this.fileUpload.fileMap.get(path);
    if (!fileUploadFile) {
      //console.log('refreshAfterBlobLoad retry', path);
      setTimeout(() => this.refreshAfterBlobLoad(path), 200);
    } else {
      //console.log('refreshAfterBlobLoad', fileUploadFile);
      this.cd.detectChanges();
    }
  }

  getSafeFileBlobPath(fileRef: IFileReference): SafeUrl {
    const fileUploadBlobPath = this.fileUpload.fileMap.get(fileRef.path)?.blobPath as string;
    if (!fileUploadBlobPath) {
      this.refreshSafeFileBlobPath(fileRef.path);
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(fileUploadBlobPath);
  }

  refreshSafeFileBlobPath(path: string) {
    const fileUploadFile = this.fileUpload.fileMap.get(path)?.blobPath as string;
    if (!fileUploadFile) {
      setTimeout(() => this.refreshSafeFileBlobPath(path), 200);
    } else {
      console.log('refreshSafeFileBlobPath', fileUploadFile);
      this.cd.detectChanges();
    }
  }

  async deleteFile(fileRef?: IFileReference) {
    fileRef ??= await firstValueFrom(this.selectedFile$);
    if (fileRef) this.fileUpload.deleteFile(fileRef);
    this.changeFileView(0);
  }

  ngOnDestroy(): void {
    // Use setTimeout to delay parent destruction slightly, allowing the child ngx-extended-pdf-viewer
    // component to complete its own cleanup first. This prevents the "startCleanup: Page X is  
    // currently rendering" error by avoiding a race condition where the parent destroys before
    // the PDF viewer finishes rendering/cleanup. This pattern is recommended in ngx-extended-pdf-viewer
    // community discussions for handling component destruction timing issues.
    setTimeout(() => {
      // Cleanup completes asynchronously
    }, 0);
  }
}
